import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { useNavigate } from 'react-router-dom'
import { colors, radius, s, ui } from '../styles/theme'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const style = {
  card: s.card,
  input: s.input,
  label: s.label,
  select: s.select
}

const SOURCE_TYPES = [
  { value: 'manual', label: 'Manuel — Ürün ID/Kod listesi' },
  { value: 'category', label: 'Kategori bazlı' },
  { value: 'segment', label: 'Segment bazlı' },
]

function SmartBlog() {
  const { selectedBrand } = useBrand()
  const navigate = useNavigate()
  const [widgets, setWidgets] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('list')
  const [editWidget, setEditWidget] = useState(null)
  const [categories, setCategories] = useState([])
  const [segments, setSegments] = useState([])
  const [saved, setSaved] = useState(false)

  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: token }

  useEffect(() => {
    if (!selectedBrand) return
    fetchAll()
  }, [selectedBrand?.id]) // eslint-disable-line

  function fetchAll() {
    setLoading(true)
    Promise.all([
      axios.get(`${API}/admin/brands/${selectedBrand.id}/smart-blog-widgets`, { headers }),
      axios.get(`${API}/admin/brands/${selectedBrand.id}/categories`, { headers }).catch(() => ({ data: { flat: [] } })),
      axios.get(`${API}/admin/brands/${selectedBrand.id}/segments`, { headers }).catch(() => ({ data: [] }))
    ]).then(([w, c, sg]) => {
      setWidgets(w.data)
      setCategories(c.data.flat || [])
      setSegments(sg.data || [])
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  function showSaved() { setSaved(true); setTimeout(() => setSaved(false), 2500) }

  async function handleDelete(id) {
    if (!window.confirm('Bu widget\'ı silmek istediğinize emin misiniz?')) return
    await axios.delete(`${API}/admin/smart-blog-widgets/${id}`, { headers })
    fetchAll()
  }

  if (!selectedBrand) return (
    <div style={{ textAlign: 'center', padding: '60px' }}>
      <p style={{ ...ui.notice, display: 'inline-block', marginBottom: '16px' }}>Önce bir marka seçin.</p>
      <button onClick={() => navigate('/brands')} style={s.btnPrimary}>Markalara Git</button>
    </div>
  )

  if (view === 'form') {
    return <WidgetForm
      brandId={selectedBrand.id}
      headers={headers}
      widget={editWidget}
      categories={categories}
      segments={segments}
      widgetToken={selectedBrand.widget_token}
      onSave={() => { fetchAll(); setView('list'); setEditWidget(null); showSaved() }}
      onCancel={() => { setView('list'); setEditWidget(null) }}
    />
  }

  return (
    <div>
      <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
        <h2 style={{ ...s.pageTitle, minWidth: 0 }}>Smart Blog — {selectedBrand.name}</h2>
        <button onClick={() => { setEditWidget(null); setView('form') }} style={s.btnPrimary}>
          + Yeni Widget
        </button>
      </div>

      {saved && <p style={{ ...ui.notice, display: 'inline-block', marginBottom: '16px' }}>Kaydedildi ✓</p>}

      {loading ? <p style={ui.emptyText}>Yükleniyor...</p> : widgets.length === 0 ? (
        <div style={{ ...style.card, ...ui.emptyText, textAlign: 'center', padding: '48px' }}>
          Henüz widget eklenmemiş.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {widgets.map(w => (
            <div key={w.id} style={{ ...style.card, marginBottom: 0 }}>
              <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
                    <p className="sx-break" style={{ fontWeight: 700, fontSize: '15px', margin: 0, minWidth: 0 }}>{w.blog_title || w.blog_url}</p>
                    <span style={ui.status(w.is_active)}><span style={ui.statusDot(w.is_active)} />{w.is_active ? 'Aktif' : 'Pasif'}</span>
                    <span style={ui.chip}>
                      {SOURCE_TYPES.find(t => t.value === w.source_type)?.label.split(' — ')[0]}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: colors.textSecondary, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <span className="sx-break" style={{ minWidth: 0, maxWidth: '100%' }}>URL: <code className="sx-break" style={{ background: colors.bgPage, border: `1px solid ${colors.borderLight}`, padding: '2px 6px', borderRadius: radius.sm, fontSize: '12px', color: colors.textPrimary, maxWidth: '100%' }}>{w.blog_url}</code></span>
                    {w.carousel_title && <span className="sx-break" style={{ minWidth: 0 }}>Carousel: {w.carousel_title}</span>}
                    {w.source_type === 'manual' && w.product_ids && <span>{w.product_ids.split(',').length} ürün</span>}
                    {w.source_type === 'category' && w.category_code && <span>Kategori: {w.category_code}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginLeft: '16px' }}>
                  <button onClick={() => { setEditWidget(w); setView('form') }} style={ui.btnSm}>Düzenle</button>
                  <button onClick={() => handleDelete(w.id)} style={ui.btnDangerQuiet}>Sil</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function WidgetForm({ brandId, headers, widget, categories, segments, widgetToken, onSave, onCancel }) {
  const [form, setForm] = useState(widget ? { ...widget } : {
    blog_url: '', blog_title: '', source_type: 'manual',
    product_ids: '', category_code: '', segment_id: '',
    carousel_title: '', is_active: true
  })
  const [saving, setSaving] = useState(false)

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function handleSave() {
    if (!form.blog_url) return alert('Blog URL zorunlu')
    setSaving(true)
    try {
      if (widget) {
        await axios.put(`${API}/admin/smart-blog-widgets/${widget.id}`, form, { headers })
      } else {
        await axios.post(`${API}/admin/brands/${brandId}/smart-blog-widgets`, form, { headers })
      }
      onSave()
    } catch (e) {
      alert('Hata: ' + (e.response?.data?.error || e.message))
    }
    setSaving(false)
  }

  return (
    <div>
      <button onClick={onCancel} style={{ ...ui.btnQuiet, marginBottom: '20px' }}>← Geri</button>

      <div style={style.card}>
        <h3 style={{ margin: '0 0 24px' }}>{widget ? 'Widget Düzenle' : 'Yeni Widget'}</h3>

        {/* Temel bilgiler */}
        <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: `1px solid ${colors.borderLight}` }}>
          <h4 style={{ ...s.sectionTitle, margin: '0 0 16px' }}>Blog Yazısı</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={style.label}>Blog URL * <span style={{ color: colors.textMuted, fontWeight: 400 }}>(kısmi eşleşme)</span></label>
              <input style={style.input} value={form.blog_url} onChange={e => set('blog_url', e.target.value)}
                placeholder="/blog/kahve-makinesi-secimi veya kahve-makinesi" />
              <p style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px' }}>Sayfanın URL'inde bu değer geçiyorsa widget gösterilir.</p>
            </div>
            <div>
              <label style={style.label}>Blog Başlığı <span style={{ color: colors.textMuted, fontWeight: 400 }}>(panelde görüntüleme için)</span></label>
              <input style={style.input} value={form.blog_title} onChange={e => set('blog_title', e.target.value)}
                placeholder="Kahve Makinesi Seçimi Rehberi" />
            </div>
          </div>
        </div>

        {/* Carousel */}
        <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: `1px solid ${colors.borderLight}` }}>
          <h4 style={{ ...s.sectionTitle, margin: '0 0 16px' }}>Carousel</h4>
          <div>
            <label style={style.label}>Carousel Başlığı <span style={{ color: colors.textMuted, fontWeight: 400 }}>(opsiyonel)</span></label>
            <input style={style.input} value={form.carousel_title} onChange={e => set('carousel_title', e.target.value)}
              placeholder="Bu Yazıda Bahsedilen Ürünler" />
          </div>
        </div>

        {/* Ürün kaynağı */}
        <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: `1px solid ${colors.borderLight}` }}>
          <h4 style={{ ...s.sectionTitle, margin: '0 0 16px' }}>Ürün Kaynağı</h4>
          <div style={{ marginBottom: '16px' }}>
            <label style={style.label}>Kaynak Tipi</label>
            <div style={{ ...ui.segmented, flexWrap: 'wrap', maxWidth: '100%' }}>
              {SOURCE_TYPES.map(t => (
                <button key={t.value} onClick={() => set('source_type', t.value)}
                  style={ui.segmentedBtn(form.source_type === t.value)}>
                  {t.label.split(' — ')[0]}
                </button>
              ))}
            </div>
            <p style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '8px' }}>
              {SOURCE_TYPES.find(t => t.value === form.source_type)?.label.split(' — ')[1]}
            </p>
          </div>

          {form.source_type === 'manual' && (
            <div>
              <label style={style.label}>Ürün ID veya Kod Listesi</label>
              <textarea style={{ ...style.input, minHeight: '80px', resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }}
                value={form.product_ids} onChange={e => set('product_ids', e.target.value)}
                placeholder="12345, 12346, 12347 veya URUN001, URUN002" />
              <p style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px' }}>Ürün ID veya ürün kodlarını virgülle ayırarak girin. T-Soft'taki ürün ID veya kod ile eşleştirilir.</p>
            </div>
          )}

          {form.source_type === 'category' && (
            <div>
              <label style={style.label}>Kategori</label>
              <select style={style.select} value={form.category_code} onChange={e => set('category_code', e.target.value)}>
                <option value="">Kategori seçin...</option>
                {categories.map(c => (
                  <option key={c.category_code} value={c.category_code}>
                    {'  '.repeat(Math.max(0, (c.level || 1) - 1))}{c.category_name} ({c.category_code})
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px' }}>Seçilen kategorideki stokta olan ürünlerden rastgele 10 ürün gösterilir.</p>
            </div>
          )}

          {form.source_type === 'segment' && (
            <div>
              <label style={style.label}>Segment</label>
              <select style={style.select} value={form.segment_id} onChange={e => set('segment_id', e.target.value)}>
                <option value="">Segment seçin...</option>
                {segments.map(sg => (
                  <option key={sg.id} value={sg.id}>{sg.name}</option>
                ))}
              </select>
              <p style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px' }}>Seçilen segment kurallarına göre ürünler filtrelenir.</p>
            </div>
          )}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '24px', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
          Aktif
        </label>

        <div className="sx-wrap" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button onClick={handleSave} disabled={saving} style={{ ...s.btnPrimary, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
          <button onClick={onCancel} style={s.btnSecondary}>İptal</button>
        </div>
      </div>
    </div>
  )
}

export default SmartBlog